import { css } from "@emotion/react";
import React from "react";
import { subordinateComponents } from "../@utils/subordinateComponents";
import { useTheme } from "../theme";
import { TableCheckbox } from "./TableCheckbox";

export interface TableRootProps extends React.HTMLAttributes<HTMLTableElement> {
  children?: React.ReactNode;
}

const Table_ = ({ ...props }: TableRootProps) => {
  return (
    <table
      css={css`
        box-sizing: border-box;
        width: 100%;
        border-spacing: 0;
        border-collapse: separate;
        isolation: isolate;
      `}
      {...props}
    />
  );
};

export interface TableHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children?: React.ReactNode;
}

const Thead = ({ ...props }: TableHeadProps) => {
  return (
    <thead
      css={css`
        box-sizing: border-box;
      `}
      {...props}
    />
  );
};

export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  /**
   * @default false
   */
  striped?: boolean;
  children?: React.ReactNode;
}

const Tbody = ({ striped, ...props }: TableBodyProps) => {
  const theme = useTheme();
  return (
    <tbody
      css={[
        css`
          box-sizing: border-box;
        `,
        striped &&
          css`
            & tr:nth-of-type(2n + 1) {
              & td {
                background-color: ${theme.palette.gray[100]};
              }
            }
          `,
      ]}
      {...props}
    />
  );
};

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  children?: React.ReactNode;
}

const BodyRow = ({ selected, ...props }: TableRowProps) => {
  const theme = useTheme();
  return (
    <tr
      css={[
        css`
          box-sizing: border-box;
        `,
        selected &&
          css`
            td,
            th {
              background-color: ${theme.palette.gray[300]};
            }
          `,
      ]}
      {...props}
    />
  );
};

export interface TableAlignProps {
  /**
   * @deprecated
   * Use useTable.args.columns[].align instead
   */
  align?: "left" | "center" | "right";
  children?: React.ReactNode;
}

export interface TableHeaderCellProps
  extends TableAlignProps, Omit<React.ComponentPropsWithoutRef<"th">, "align"> {
  children?: React.ReactNode;
}

const TableHeaderCell = ({ align, ...props }: TableHeaderCellProps) => {
  const theme = useTheme();
  return (
    <th
      css={[
        css`
          box-sizing: border-box;
          padding: ${theme.spacing * 1.5}px;
          font-weight: bolder;
          text-align: ${align};
          white-space: nowrap;
          background-color: white;
          border-top: 2px solid ${theme.palette.gray[300]};
          border-bottom: 2px solid ${theme.palette.gray[300]};
          &:last-of-type {
            border-right: 2px solid ${theme.palette.gray[300]};
          }
          &:first-of-type {
            border-left: 2px solid ${theme.palette.gray[300]};
          }
        `,
        theme.typography.body,
      ]}
      {...props}
    />
  );
};

export interface TableDataCellProps
  extends TableAlignProps, Omit<React.ComponentPropsWithoutRef<"td">, "align"> {
  children?: React.ReactNode;
}

const TableDataCell = ({ align, ...props }: TableDataCellProps) => {
  const theme = useTheme();
  return (
    <td
      css={[
        css`
          box-sizing: border-box;
          padding: ${theme.spacing * 1.5}px;
          text-align: ${align};
          border-bottom: 1px solid ${theme.palette.gray[300]};
          &:last-of-type {
            border-right: 2px solid ${theme.palette.gray[300]};
          }
          &:first-of-type {
            border-left: 2px solid ${theme.palette.gray[300]};
          }
        `,
        theme.typography.body,
      ]}
      {...props}
    />
  );
};

/**
 * @deprecated Use TableUI instead
 */
export const Table = subordinateComponents(Table_, {
  Header: Thead,
  Body: Tbody,
  Row: BodyRow,
  HeaderCell: TableHeaderCell,
  Cell: TableDataCell,
  Checkbox: TableCheckbox,
});
